/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * OnboardingWizard.tsx — localized first-run tour for the operator workspace.
 * It explains the stable workspace regions and records completion locally so
 * returning operators are not interrupted. It does not provision an account,
 * configure queues, or replace product documentation.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '@/i18n';

const ONBOARDING_KEY = 'ai_cc_onboarding_completed';

interface Step {
  titleKey: string;
  descriptionKey: string;
  target: string; // CSS selector or area name
  position: 'center' | 'left' | 'right' | 'top' | 'bottom';
}

const STEPS: Step[] = [
  {
    titleKey: 'onboarding.welcome_title',
    descriptionKey: 'onboarding.welcome_description',
    target: 'center',
    position: 'center',
  },
  {
    titleKey: 'onboarding.navigation_title',
    descriptionKey: 'onboarding.navigation_description',
    target: 'left-rail',
    position: 'right',
  },
  {
    titleKey: 'onboarding.workspace_title',
    descriptionKey: 'onboarding.workspace_description',
    target: 'center-workspace',
    position: 'center',
  },
  {
    titleKey: 'onboarding.assistant_title',
    descriptionKey: 'onboarding.assistant_description',
    target: 'right-copilot',
    position: 'left',
  },
  {
    titleKey: 'onboarding.controls_title',
    descriptionKey: 'onboarding.controls_description',
    target: 'top-bar',
    position: 'bottom',
  },
];

export function OnboardingWizard() {
  const t = useTranslation();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      // Show after a short delay to let the app render
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      <div
        className="glass-panel"
        style={{
          maxWidth: 480,
          width: '90%',
          padding: 'var(--space-2xl)',
          textAlign: 'center',
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        {/* Progress dots */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 'var(--space-lg)',
          }}
        >
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: i === step ? 'rgba(34, 197, 94, 1)' : 'var(--toggle-bg)',
                border: i === step ? '2px solid rgba(34, 197, 94, 0.5)' : '2px solid transparent',
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </div>

        {/* Step counter */}
        <div
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-muted)',
            marginBottom: 'var(--space-sm)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('onboarding.step', { current: step + 1, total: STEPS.length })}
        </div>

        {/* Title */}
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-xl)',
            fontWeight: 700,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-md)',
          }}
        >
          {t(currentStep.titleKey)}
        </h2>

        {/* Description */}
        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-base)',
            lineHeight: 'var(--leading-relaxed)',
            marginBottom: 'var(--space-xl)',
          }}
        >
          {t(currentStep.descriptionKey)}
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'center' }}>
          <button
            className="glass-ghost-compact"
            onClick={handleSkip}
            style={{ fontSize: 'var(--text-sm)' }}
          >
            {t('onboarding.skip')}
          </button>
          <button
            className="glass-button"
            onClick={handleNext}
            style={{ minWidth: 140, justifyContent: 'center' }}
          >
            {isLast ? t('onboarding.start') : t('onboarding.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
